<template>
  <!-- prettier-ignore -->
  <div class="Package">
    <div class="heading">
      <h1>{{ item.name }}</h1>
      <div
        v-if="available"
        class="price"
      >
        {{ getLabel('detail_price', { price }) }}
        <the-icon
          v-tooltip="offerText"
          art="light"
          name="circle-info"
        />
      </div>
    </div>
    <div class="content">
      <component
        :is="folder"
        v-if="available"
        :products-tree="productsByCategory"
        @opened="onOpenedCategory"
      />
      <div
        v-else
        class="unavailable"
      >
        <h3 v-text="getLabel('preview_notavailable_title')" />
        <p v-text="getLabel('preview_notavailable_text')" />
        <a
          v-if="!skipPackagesOverview"
          class="global-sanitas-link"
          @click="emit('backToBundle')"
          v-text="getLabel('detail_link_otherpackages')"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import isNil from 'lodash/isNil'

import { currency } from '@/modules/PartnerWidget/helpers/formatters'
import { getRouteData, getPackageTrackingId } from '@/modules/PartnerWidget/helpers/tracking'
import { CATEGORY_ADDITIONAL, CATEGORY_FURTHER_ADDITIONAL } from '@/modules/PartnerWidget/constants'
import { EVENT_PARTNER_WIDGET } from '@/config/events'
import { events$ } from '@/services'

// Components
import TabsContainer from '@/modules/PartnerWidget/components/TabsContainer'
import PartnerWidgetAccordion from '@/modules/PartnerWidget/components/PartnerWidgetAccordion'

// Hooks
import useCategories from '@/hooks/useCategories'
import usePartnerWidget from '@/hooks/usePartnerWidget'

const { getSortedCategories } = useCategories()
const {
  getLabel,
  getPackageTotalPrice,
  getProductPriceOptionForFranchise,
  getProductPrice,
  getSortedProductsFromCategory,
  getOfferText,
  partnerWidgetDisplayMode,
} = usePartnerWidget()

// Props
const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  available: {
    type: Boolean,
    default: true,
  },
  skipEntryPage: {
    type: Boolean,
    default: false,
  },
  skipPackagesOverview: {
    type: Boolean,
    default: false,
  },
})

// Init
const emit = defineEmits(['backToBundle'])

// Data
const packageTrackingId = ref('')
const useAccordion = ref(null)

// Computed
const firstPerson = computed(() => {
  return props.item.basket?.persons[0]
})

const offerText = computed(() => {
  return getOfferText(props.item.basket.persons, props.item.showExampleOfferText)
})

const price = computed(() => {
  return currency(getPackageTotalPrice(props.item.products, firstPerson.value))
})

const productsIds = computed(() => {
  return props.item.products.map(p => p.id)
})

const productsByCategory = computed(() => {
  const sortedCategories = getSortedCategories(props.item.productsStructure.categories)
  return sortedCategories.reduce((acc, category) => {
    const categoryId = category.id
    const packageProducts = getSortedProductsFromCategory(props.item.productsStructure, categoryId)
      .filter(product => productsIds.value.includes(product.productId))
      .map(product => {
        // packageId used for tracking
        product.packageId = props.item.id
        // set product price
        const bundleProduct = props.item.products.find(prod => prod.id === product.productId)
        const value = !isNil(bundleProduct.value) ? parseInt(bundleProduct.value) : null
        if (!isNil(value)) {
          product.price = getProductPriceOptionForFranchise(firstPerson.value, product.productId, value).price
        } else {
          product.price = getProductPrice(firstPerson.value, product.productId).price
        }
        return product
      })
    if (packageProducts.length > 0) {
      if (categoryId === CATEGORY_FURTHER_ADDITIONAL) {
        acc[CATEGORY_ADDITIONAL] = acc[CATEGORY_ADDITIONAL] || []
        acc[CATEGORY_ADDITIONAL].push(...packageProducts)
        return acc
      }
      acc[categoryId] = packageProducts
    }
    return acc
  }, {})
})

const folder = computed(() => {
  if (useAccordion.value === null) {
    return null
  }
  return useAccordion.value ? PartnerWidgetAccordion : TabsContainer
})

// Methods
function handleResize() {
  const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  useAccordion.value = partnerWidgetDisplayMode.value === 'narrow' || width <= 600
}

function handleTracking() {
  // if detail is first page, track only after first interaction
  if (props.skipEntryPage && props.skipPackagesOverview) {
    return
  }
  packageTrackingId.value = getPackageTrackingId(props.item)
  const trackingPayload = getRouteData({
    title: 'BundleDetail',
    routes: ['bundles', 'detail', props.item.basket?.partnerWidgetBundleCode, packageTrackingId.value],
  })

  if (props.skipEntryPage || props.skipPackagesOverview) {
    events$.emit(EVENT_PARTNER_WIDGET.DISPLAYED, trackingPayload)
    // displayMode directly tracks opened category, not overview
    if (partnerWidgetDisplayMode.value === 'narrow') {
      events$.emit(EVENT_PARTNER_WIDGET.PAGE_VIEWED, trackingPayload)
    }
  } else if (partnerWidgetDisplayMode.value === 'narrow') {
    events$.emit(EVENT_PARTNER_WIDGET.PAGE_VIEWED, trackingPayload)
  }
}

function onOpenedCategory(category) {
  const detailOpenedCategoryPayload = getRouteData({
    title: 'BundleDetail' + category,
    routes: ['bundles', 'detail', props.item.basket?.partnerWidgetBundleCode, packageTrackingId.value, category],
  })
  events$.emit(EVENT_PARTNER_WIDGET.DISPLAYED, detailOpenedCategoryPayload)
  events$.emit(EVENT_PARTNER_WIDGET.PAGE_VIEWED, detailOpenedCategoryPayload)
}

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('resize', handleResize)
  handleResize()
  handleTracking()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.Package {
  display: flex;
  flex-direction: column;

  .wide & {
    width: 600px;
    margin: 0 auto;
    flex: 1 0 auto;
  }

  .heading {
    .narrow & {
      margin-top: 10px;
    }

    .wide & {
      display: flex;
      align-items: flex-end;
      margin-bottom: 30px;
    }

    h1 {
      font-size: var(--pw-font-size-large);
      line-height: var(--pw-line-height-large);
      font-weight: 700;
      text-align: center;
      margin: 0;

      .wide & {
        font-size: var(--pw-font-size-heading-wide);
        line-height: 35px;
        text-align: left;
        margin-right: 10px;
      }
    }

    .price {
      text-align: center;
      margin-bottom: 10px;

      .wide & {
        margin-bottom: 0;
      }
    }
  }

  .content {
    background-color: var(--c-primary-neutral-3);
    flex: 1;
    display: flex;
    flex-direction: column;

    .narrow & {
      overflow: auto;

      &::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.3);
        background-color: var(--c-primary-neutral-2);
      }

      &::-webkit-scrollbar {
        width: 5px;
        background-color: var(--c-primary-neutral-2);
      }

      &::-webkit-scrollbar-thumb {
        background-color: var(--c-primary-color-3);
      }
    }

    .product {
      &:not(:last-child) {
        border-bottom: 1px solid var(--c-secondary-neutral-3);
      }
    }

    .unavailable {
      padding: 20px;
    }
  }

  h1 {
    color: var(--c-primary-neutral-1);
  }
}
</style>
